import React, { useState, useEffect } from 'react';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Target,
  Activity,
  Clock,
  AlertCircle,
  Trophy,
  XCircle,
  BarChart3,
  X,
} from 'lucide-react';
import { useBrand } from '../contexts/BrandContext';
import { analyticsService } from '../lib/analyticsService';
import type { StageMetrics, WinLossMetrics, ActivityMetrics, RevenueForecast } from '../lib/analyticsService';

interface PipelineAnalyticsProps {
  onClose: () => void;
  brandIds: string[];
}

export default function PipelineAnalytics({ onClose, brandIds }: PipelineAnalyticsProps) {
  const { brands } = useBrand();
  const [loading, setLoading] = useState(true);
  const [stageMetrics, setStageMetrics] = useState<StageMetrics[]>([]);
  const [winLossMetrics, setWinLossMetrics] = useState<WinLossMetrics | null>(null);
  const [activityMetrics, setActivityMetrics] = useState<ActivityMetrics | null>(null);
  const [forecast, setForecast] = useState<RevenueForecast[]>([]);

  useEffect(() => {
    if (brandIds.length > 0) {
      loadAnalytics();
    }
  }, [brandIds]);

  const loadAnalytics = async () => {
    if (brandIds.length === 0) return;

    try {
      setLoading(true);
      const allStageMetrics: StageMetrics[] = [];
      let consolidatedWinLoss: WinLossMetrics = {
        totalOpportunities: 0,
        wonCount: 0,
        lostCount: 0,
        activeCount: 0,
        winRate: 0,
        lossRate: 0,
        totalWonValue: 0,
        totalLostValue: 0,
        averageWonValue: 0,
        lossReasons: [],
      };
      let consolidatedActivity: ActivityMetrics = {
        totalActivities: 0,
        callCount: 0,
        emailCount: 0,
        meetingCount: 0,
        noteCount: 0,
        averageActivitiesPerOpportunity: 0,
        activitiesByType: [],
      };
      const allForecasts: RevenueForecast[] = [];

      for (const brandId of brandIds) {
        const [stages, winLoss, activities, revenueForecast] = await Promise.all([
          analyticsService.getStageMetrics(brandId),
          analyticsService.getWinLossMetrics(brandId),
          analyticsService.getActivityMetrics(brandId),
          analyticsService.getRevenueForecast(brandId),
        ]);

        allStageMetrics.push(...stages);

        consolidatedWinLoss.totalOpportunities += winLoss.totalOpportunities;
        consolidatedWinLoss.wonCount += winLoss.wonCount;
        consolidatedWinLoss.lostCount += winLoss.lostCount;
        consolidatedWinLoss.activeCount += winLoss.activeCount;
        consolidatedWinLoss.totalWonValue += winLoss.totalWonValue;
        consolidatedWinLoss.totalLostValue += winLoss.totalLostValue;

        consolidatedActivity.totalActivities += activities.totalActivities;
        consolidatedActivity.callCount += activities.callCount;
        consolidatedActivity.emailCount += activities.emailCount;
        consolidatedActivity.meetingCount += activities.meetingCount;
        consolidatedActivity.noteCount += activities.noteCount;

        allForecasts.push(...revenueForecast);
      }

      const closedCount = consolidatedWinLoss.wonCount + consolidatedWinLoss.lostCount;
      consolidatedWinLoss.winRate = closedCount > 0 ? (consolidatedWinLoss.wonCount / closedCount) * 100 : 0;
      consolidatedWinLoss.lossRate = closedCount > 0 ? (consolidatedWinLoss.lostCount / closedCount) * 100 : 0;
      consolidatedWinLoss.averageWonValue = consolidatedWinLoss.wonCount > 0 ? consolidatedWinLoss.totalWonValue / consolidatedWinLoss.wonCount : 0;
      consolidatedActivity.averageActivitiesPerOpportunity = consolidatedWinLoss.totalOpportunities > 0 ? consolidatedActivity.totalActivities / consolidatedWinLoss.totalOpportunities : 0;

      const forecastMap = new Map<string, RevenueForecast>();
      allForecasts.forEach(f => {
        const existing = forecastMap.get(f.period);
        if (existing) {
          existing.expectedRevenue += f.expectedRevenue;
          existing.weightedRevenue += f.weightedRevenue;
          existing.opportunityCount += f.opportunityCount;
        } else {
          forecastMap.set(f.period, { ...f });
        }
      });

      setStageMetrics(allStageMetrics);
      setWinLossMetrics(consolidatedWinLoss);
      setActivityMetrics(consolidatedActivity);
      setForecast(Array.from(forecastMap.values()).sort((a, b) => a.period.localeCompare(b.period)));
    } catch (error) {
      console.error('Error loading all brands analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercent = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg max-w-7xl w-full my-8">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-lg">
          <div className="flex items-center gap-3">
            <BarChart3 className="w-6 h-6 text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-900">Pipeline Analytics</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-6 space-y-6 max-h-[calc(100vh-120px)] overflow-y-auto">
          {winLossMetrics && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Win/Loss Overview</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 border border-green-200">
                  <div className="flex items-center justify-between mb-2">
                    <Trophy className="w-5 h-5 text-green-600" />
                    <span className="text-2xl font-bold text-green-700">
                      {winLossMetrics.wonCount}
                    </span>
                  </div>
                  <p className="text-sm text-green-800 font-medium">Won Deals</p>
                  <p className="text-xs text-green-600 mt-1">{formatCurrency(winLossMetrics.totalWonValue)}</p>
                </div>

                <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-lg p-4 border border-red-200">
                  <div className="flex items-center justify-between mb-2">
                    <XCircle className="w-5 h-5 text-red-600" />
                    <span className="text-2xl font-bold text-red-700">
                      {winLossMetrics.lostCount}
                    </span>
                  </div>
                  <p className="text-sm text-red-800 font-medium">Lost Deals</p>
                  <p className="text-xs text-red-600 mt-1">{formatCurrency(winLossMetrics.totalLostValue)}</p>
                </div>

                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
                  <div className="flex items-center justify-between mb-2">
                    <Target className="w-5 h-5 text-blue-600" />
                    <span className="text-2xl font-bold text-blue-700">
                      {formatPercent(winLossMetrics.winRate)}
                    </span>
                  </div>
                  <p className="text-sm text-blue-800 font-medium">Win Rate</p>
                  <p className="text-xs text-blue-600 mt-1">Of closed deals</p>
                </div>

                <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 border border-purple-200">
                  <div className="flex items-center justify-between mb-2">
                    <DollarSign className="w-5 h-5 text-purple-600" />
                    <span className="text-2xl font-bold text-purple-700">
                      {formatCurrency(winLossMetrics.averageWonValue)}
                    </span>
                  </div>
                  <p className="text-sm text-purple-800 font-medium">Avg Won Deal</p>
                  <p className="text-xs text-purple-600 mt-1">Per opportunity</p>
                </div>
              </div>

              {winLossMetrics.lossReasons.length > 0 && (
                <div className="mt-4 bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Top Loss Reasons</h4>
                  <div className="space-y-2">
                    {winLossMetrics.lossReasons.slice(0, 5).map((reason) => (
                      <div key={reason.reason} className="flex items-center justify-between">
                        <span className="text-sm text-gray-700">{reason.reason}</span>
                        <span className="text-sm font-medium text-gray-900">{reason.count} deals</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Stage Performance</h3>
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Stage</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase">Count</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase">Total Value</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase">Avg Value</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase">Avg Days</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase">Conversion</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {stageMetrics.map((stage, index) => {
                    const isBottleneck = stage.averageDaysInStage > 14;
                    return (
                      <tr key={stage.stageId} className={isBottleneck ? 'bg-yellow-50' : ''}>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          <div className="flex items-center gap-2">
                            {stage.stageName}
                            {isBottleneck && (
                              <AlertCircle className="w-4 h-4 text-yellow-600" title="Potential bottleneck" />
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-right text-gray-700">{stage.count}</td>
                        <td className="px-4 py-3 text-sm text-right font-medium text-gray-900">
                          {formatCurrency(stage.totalValue)}
                        </td>
                        <td className="px-4 py-3 text-sm text-right text-gray-700">
                          {formatCurrency(stage.averageValue)}
                        </td>
                        <td className="px-4 py-3 text-sm text-right text-gray-700">
                          {stage.averageDaysInStage.toFixed(1)} days
                        </td>
                        <td className="px-4 py-3 text-sm text-right">
                          {index < stageMetrics.length - 1 ? (
                            <span className={`font-medium ${stage.conversionRate >= 50 ? 'text-green-600' : 'text-red-600'}`}>
                              {formatPercent(stage.conversionRate)}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {activityMetrics && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Activity Metrics</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <Activity className="w-5 h-5 text-blue-600" />
                    <span className="text-2xl font-bold text-gray-900">
                      {activityMetrics.totalActivities}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">Total Activities</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {activityMetrics.averageActivitiesPerOpportunity.toFixed(1)} per opportunity
                  </p>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-2xl">📞</span>
                    <span className="text-2xl font-bold text-gray-900">
                      {activityMetrics.callCount}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">Calls</p>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-2xl">📧</span>
                    <span className="text-2xl font-bold text-gray-900">
                      {activityMetrics.emailCount}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">Emails</p>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-2xl">🤝</span>
                    <span className="text-2xl font-bold text-gray-900">
                      {activityMetrics.meetingCount}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">Meetings</p>
                </div>
              </div>
            </div>
          )}

          {forecast.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Revenue Forecast</h3>
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Period</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase">Expected</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase">Weighted</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase">Deals</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {forecast.map((period) => (
                      <tr key={period.period}>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          {new Date(period.period + '-01').toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                          })}
                        </td>
                        <td className="px-4 py-3 text-sm text-right text-gray-700">
                          {formatCurrency(period.expectedRevenue)}
                        </td>
                        <td className="px-4 py-3 text-sm text-right font-medium text-blue-700">
                          {formatCurrency(period.weightedRevenue)}
                        </td>
                        <td className="px-4 py-3 text-sm text-right text-gray-700">
                          {period.opportunityCount}
                        </td>
                      </tr>
                    ))}
                    <tr className="bg-gray-50 font-semibold">
                      <td className="px-4 py-3 text-sm text-gray-900">Total</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-900">
                        {formatCurrency(forecast.reduce((sum, p) => sum + p.expectedRevenue, 0))}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-blue-700">
                        {formatCurrency(forecast.reduce((sum, p) => sum + p.weightedRevenue, 0))}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-gray-900">
                        {forecast.reduce((sum, p) => sum + p.opportunityCount, 0)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
